import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, FileText, Link2, Loader2, UploadCloud, Users, X } from "lucide-react";
import api, { apiErrorMessage } from "../lib/api";
import { ACCEPT_ATTR, ALLOWED_LABEL, fileProblem, formatBytes } from "../lib/portalFiles";
import { dueLine, fromDeadlineParts, kindOf, linkProblem, stateOf, toDeadlineParts } from "../lib/portalKinds";
import DeadlineField from "../components/portal/DeadlineField";
import AttendanceFields from "../components/portal/AttendanceFields";
import { parseHours } from "../lib/portalAttendance";
import { uploadErrorText, uploadFile } from "../lib/portalUpload";
import { Chip, fieldCls, primaryBtn, secondaryBtn, Shell } from "../components/portal/PortalShell";
import SubmissionHistory, { downloadFile, hostOf, LinkList } from "../components/portal/SubmissionHistory";
import { isFped } from "../lib/regime";
import LibraryControls from "../components/portal/LibraryControls";
import { FormattedText, FormattedTextarea } from "../components/portal/FormattedText";
import { isLibrary, LIBRARY_KIND_LABELS } from "../lib/courseLessons";

/**
 * `/portal/tema/:activityId` — one activity. The participant hands in;
 * the group's trainer edits, publishes or archives.
 */
export default function PortalActivity() {
  const { activityId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setData((await api.get(`/portal/activities/${activityId}`)).data);
      setError("");
    } catch (err) {
      setError(err?.response?.status === 401 ? "out"
        : apiErrorMessage(err, "Nu am găsit tema."));
    }
  }, [activityId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Shell wide>
      <Link to="/portal" className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#965EA7]">
        <ArrowLeft size={14} /> Înapoi la grupe
      </Link>
      {error === "out" && (
        <p className="mt-8 text-sm text-[#373A45]">
          Sesiunea s-a încheiat. <Link to="/portal" className="font-semibold text-[#965EA7]">Intră din nou</Link>.
        </p>
      )}
      {error && error !== "out" && <p className="mt-8 text-sm text-[#c0483f]" role="alert">{error}</p>}
      {!data && !error && <div className="py-24 text-center text-[#6B7280]"><Loader2 className="mx-auto animate-spin" /></div>}
      {data && <ActivityBody data={data} reload={load} />}
    </Shell>
  );
}

function ActivityBody({ data, reload }) {
  const a = data.activity;
  const kind = kindOf(a);
  const lib = isLibrary(a);
  const trainer = data.role === "trainer";
  return (
    <article className="mt-6">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
        {lib ? `${a.theme ? `Lecția ${a.theme} · ` : ""}${LIBRARY_KIND_LABELS[a.library_kind] || kind?.label}` : kind?.label || "Activitate"}
      </p>
      <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-[#373A45] [text-wrap:balance]">{a.title}</h1>
      {kind?.submits && <p className="mt-2 text-sm text-[#6B7280]">{dueLine(a.due_at)}</p>}
      {a.description && (
        <div className="mt-6 rounded-2xl border border-[#E5E7EB] bg-white p-5 text-[15px] leading-relaxed text-[#373A45]">
          <FormattedText text={a.description} />
        </div>
      )}
      {lib && a.trainer_note && (
        <div className="mt-3 rounded-2xl bg-[#F0E7F4] p-4 text-sm text-[#373A45]" data-testid="portal-trainer-note">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#7A4986]">Nota formatorului</p>
          <p className="mt-1 whitespace-pre-line">{a.trainer_note}</p>
        </div>
      )}
      {lib && kind?.submits && !trainer && ((a.files || []).length > 0 || (a.links || []).length > 0) && <Material activity={a} />}
      {lib && trainer && <LibraryControls data={data} reload={reload} />}
      {!kind?.submits && !trainer && <Material activity={a} />}
      {!lib && !kind?.submits && trainer && <MaterialEditor data={data} reload={reload} />}
      {!lib && kind?.submits && trainer && <TrainerPanel data={data} reload={reload} />}
      {kind?.submits && !trainer && <Submission data={data} reload={reload} />}
    </article>
  );
}

// ------------------------------------------------------------- participant

function Submission({ data, reload }) {
  const { activity, submission: sub, read_only: readOnly, limits } = data;
  const [draft, setDraft] = useState(() => ({ files: sub.draft.files, links: sub.draft.links, note: sub.draft.note }));
  const [dirty, setDirty] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // {tone, text}
  const [removed, setRemoved] = useState(() => new Set());
  // Once something is handed in, the history comes first and the editor opens
  // on request — a submitted task should not look like one still to do.
  const [editing, setEditing] = useState(() => sub.versions.length === 0 || sub.draft_changed);
  const inputRef = useRef(null);

  // Files are attached by the server as they finish uploading; take its list,
  // keep what is being typed and what was taken out but not saved yet.
  useEffect(() => {
    setDraft((d) => ({ ...d, files: sub.draft.files.filter((f) => !removed.has(f.id)) }));
  }, [sub.draft.files, removed]);

  const locked = sub.locked || readOnly;
  const st = stateOf({ state: sub.status, overdue: false });
  const change = (patch) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); setMessage(null); };

  const addFiles = async (list) => {
    const chosen = Array.from(list || []);
    const room = limits.max_files - draft.files.length;
    for (const [i, file] of chosen.entries()) {
      const key = `${Date.now()}-${i}`;
      const problem = i >= room ? `Cel mult ${limits.max_files} fișiere pe predare.` : fileProblem(file);
      setUploads((u) => [...u, { key, name: file.name, pct: problem ? null : 0, error: problem }]);
      if (problem) continue; // eslint-disable-line no-continue
      try {
        await uploadFile(`/portal/activities/${activity.id}/submission/uploads`, file, { // eslint-disable-line no-await-in-loop
          onProgress: (pct) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct } : x))),
        });
        setUploads((u) => u.filter((x) => x.key !== key));
      } catch (err) {
        setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct: null, error: uploadErrorText(err, apiErrorMessage) } : x)));
      }
    }
    await reload();
  };

  const addLink = () => {
    const problem = linkProblem(linkUrl);
    if (problem) { setMessage({ tone: "bad", text: problem }); return; }
    if (draft.links.length >= limits.max_links) {
      setMessage({ tone: "bad", text: `Cel mult ${limits.max_links} linkuri pe predare.` });
      return;
    }
    change({ links: [...draft.links, { url: linkUrl.trim(), label: linkLabel.trim() }] });
    setLinkUrl("");
    setLinkLabel("");
  };

  const save = async () => {
    await api.put(`/portal/activities/${activity.id}/submission/draft`, {
      files: draft.files.map((f) => f.id), links: draft.links.map(({ url, label }) => ({ url, label })), note: draft.note,
    });
    setDirty(false);
    setRemoved(new Set());
  };

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      setMessage({ tone: "good", text: ok });
      await reload();
    } catch (err) {
      setMessage({ tone: "bad", text: apiErrorMessage(err, "Nu am putut salva.") });
    } finally {
      setBusy(false);
    }
  };

  const handIn = () => run(async () => {
    await save();
    await api.post(`/portal/activities/${activity.id}/submission/submit`);
    setEditing(false);
  }, "Ai predat. Formatorul vede acum predarea ta.");

  const hasContent = draft.files.length > 0 || draft.links.length > 0;
  const canHandIn = hasContent && (dirty || sub.draft_changed || sub.versions.length === 0);

  return (
    <section className="mt-8" aria-labelledby="portal-submission">
      <div className="flex flex-wrap items-center gap-3">
        <h2 id="portal-submission" className="font-display text-xl font-bold text-[#373A45]">Predarea ta</h2>
        <Chip tone={st.tone}>{st.label}</Chip>
      </div>
      {sub.status === "needs_work" && !readOnly && (
        <p className="mt-3 rounded-xl border border-[#d99a2b]/40 bg-[#d99a2b]/10 px-4 py-3 text-sm text-[#373A45]">
          Formatorul ți-a cerut o versiune nouă. Citește feedbackul de mai jos, apoi pregătește versiunea nouă.
        </p>
      )}
      {readOnly && <p className="mt-3 text-sm text-[#373A45]">Înscrierea ta s-a încheiat: poți vedea și descărca ce ai predat.</p>}
      {sub.locked && <p className="mt-3 text-sm text-[#373A45]">{sub.locked}</p>}

      {!locked && !editing && (
        <button type="button" onClick={() => setEditing(true)} className={`mt-4 ${secondaryBtn}`}
          data-testid="portal-new-version">
          Pregătește o versiune nouă
        </button>
      )}

      {!locked && editing && (
        <div className="mt-4 grid gap-5 rounded-2xl border border-[#E5E7EB] bg-white p-5" data-testid="portal-submission-editor">
          <div>
            <p className="text-sm font-semibold text-[#373A45]">Fișiere</p>
            {draft.files.length > 0 && (
              <ul className="mt-2 grid gap-1.5">
                {draft.files.map((f) => (
                  <li key={f.id} className="flex items-center gap-2 rounded-lg bg-[#F5F6F6] px-3 py-2 text-sm">
                    <FileText size={15} className="shrink-0 text-[#6B7280]" />
                    <span className="min-w-0 flex-1 truncate text-[#373A45]">{f.name}</span>
                    <span className="text-xs tabular-nums text-[#6B7280]">{formatBytes(f.size)}</span>
                    <button type="button" onClick={() => { setRemoved((r) => new Set(r).add(f.id)); setDirty(true); }}
                      aria-label={`Scoate ${f.name}`} className="rounded p-1 text-[#6B7280] hover:text-[#c0483f]">
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {uploads.map((u) => (
              <p key={u.key} className={`mt-1.5 text-xs ${u.error ? "text-[#c0483f]" : "text-[#6B7280]"}`}>
                {u.name}: {u.error || `${u.pct}%`}
              </p>
            ))}
            <button type="button" onClick={() => inputRef.current?.click()} className={`mt-2 ${secondaryBtn}`}
              data-testid="portal-submission-pick">
              <UploadCloud size={14} /> Adaugă fișiere
            </button>
            <p className="mt-1 text-xs text-[#6B7280]">{ALLOWED_LABEL}. Până la 50 MB pe fișier.</p>
            <input ref={inputRef} type="file" multiple accept={ACCEPT_ATTR} className="hidden"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="portal-submission-input" />
          </div>

          <div>
            <p className="text-sm font-semibold text-[#373A45]">Linkuri</p>
            {draft.links.length > 0 && (
              <ul className="mt-2 grid gap-1.5">
                {draft.links.map((ln, i) => (
                  <li key={`${ln.url}-${i}`} className="flex items-center gap-2 rounded-lg bg-[#F5F6F6] px-3 py-2 text-sm">
                    <Link2 size={15} className="shrink-0 text-[#6B7280]" />
                    <span className="min-w-0 flex-1 truncate text-[#373A45]">{ln.label || ln.url}</span>
                    <span className="text-xs text-[#6B7280]">{hostOf(ln.url)}</span>
                    <button type="button" onClick={() => change({ links: draft.links.filter((_, j) => j !== i) })}
                      aria-label="Scoate linkul" className="rounded p-1 text-[#6B7280] hover:text-[#c0483f]">
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_12rem_auto]">
              <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://www.canva.com/…"
                className={fieldCls} inputMode="url" data-testid="portal-link-url" />
              <input value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} placeholder="Ce e (opțional)"
                maxLength={120} className={fieldCls} />
              <button type="button" onClick={addLink} className={secondaryBtn} data-testid="portal-link-add">Adaugă</button>
            </div>
            <p className="mt-1.5 text-xs text-[#6B7280]">
              Verifică întâi că linkul se deschide pentru oricine are adresa. Noi nu vedem setările de acces din
              Canva sau Drive, iar un link închis ajunge la formator ca o predare goală.
            </p>
          </div>

          <label className="grid gap-1 text-sm font-semibold text-[#373A45]">
            <span>Notă pentru formator <span className="font-normal text-[#6B7280]">(opțional)</span></span>
            <textarea value={draft.note} onChange={(e) => change({ note: e.target.value })} rows={3} maxLength={4000}
              className={fieldCls} />
          </label>

          {message && (
            <p className={`text-sm ${message.tone === "bad" ? "text-[#c0483f]" : "text-[#3c7457]"}`} role="status">{message.text}</p>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={handIn} disabled={busy || !canHandIn} className={primaryBtn} data-testid="portal-hand-in">
              {busy && <Loader2 size={15} className="animate-spin" />}
              {sub.versions.length ? "Predă versiunea nouă" : "Predă"}
            </button>
            <button type="button" onClick={() => run(save, "Am salvat. Poți preda când ești gata.")}
              disabled={busy || !dirty} className={secondaryBtn}>
              Salvează fără să predai
            </button>
          </div>
        </div>
      )}

      {!editing && message && (
        <p className={`mt-3 text-sm ${message.tone === "bad" ? "text-[#c0483f]" : "text-[#3c7457]"}`} role="status">{message.text}</p>
      )}

      <SubmissionHistory versions={sub.versions} feedback={sub.feedback} title="Ce ai predat" />
    </section>
  );
}

// ---------------------------------------------------------------- trainer

function TrainerPanel({ data, reload }) {
  const a = data.activity;
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: a.title, description: a.description || "", due: toDeadlineParts(a.due_at) });
  const [att, setAtt] = useState({ counts: Boolean(a.counts_for_attendance), hours: a.hours ? String(a.hours) : "",
    theme: a.theme ? String(a.theme) : "", component: a.component || "practical" });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const patch = async (body, ok, after) => {
    setBusy(true);
    try {
      await api.patch(`/portal/activities/${a.id}`, body);
      setMessage({ tone: "good", text: ok });
      if (after) after(); else await reload();
    } catch (err) {
      setMessage({ tone: "bad", text: apiErrorMessage(err, "Nu am putut salva.") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-8 grid gap-4" data-testid="portal-trainer-activity">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-xl font-bold text-[#373A45]">Pentru formator</h2>
        <Chip tone={a.published ? "good" : "neutral"}>{a.published ? "Publicată" : "Nepublicată"}</Chip>
        <span className="text-sm text-[#6B7280]">
          {data.submitted_count === 1 ? "o predare" : `${data.submitted_count} predări`}
        </span>
      </div>
      <Link to={`/portal/grupa/${a.group_id}`}
        className="flex items-center gap-3 rounded-xl bg-[#965EA7]/[0.07] px-4 py-3 text-sm font-semibold text-[#7A4986] hover:bg-[#965EA7]/[0.12]">
        <Users size={16} /> Predările cursanților și feedbackul
        <ChevronRight size={16} className="ml-auto" />
      </Link>

      <div className="grid gap-3 rounded-2xl border border-[#E5E7EB] bg-white p-5">
        <label className="grid gap-1 text-sm font-semibold text-[#373A45]">
          Titlu
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={160} className={fieldCls} />
        </label>
        <label className="grid gap-1 text-sm font-semibold text-[#373A45]">
          Ce au de făcut
          <FormattedTextarea value={form.description} onChange={(description) => setForm({ ...form, description })} rows={8} className={fieldCls} />
        </label>
        <div className="grid gap-1 text-sm font-semibold text-[#373A45]">
          Termen
          <DeadlineField value={form.due} onChange={(due) => setForm({ ...form, due })} />
          <span className="text-xs font-normal text-[#6B7280]">Ora României.</span>
        </div>
        {!isFped(data) && <AttendanceFields value={att} onChange={setAtt} kind="assignment" />}
        {message && (
          <p className={`text-sm ${message.tone === "bad" ? "text-[#c0483f]" : "text-[#3c7457]"}`} role="status">{message.text}</p>
        )}
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy} className={primaryBtn}
            onClick={() => patch({ title: form.title, description: form.description, due_at: fromDeadlineParts(form.due.date, form.due.time),
              counts_for_attendance: att.counts, hours: att.counts ? parseHours(att.hours) || 0 : 0,
        theme: att.theme ? Number(att.theme) : null, component: att.component || "practical" }, "Am salvat.")}>
            Salvează
          </button>
          <button type="button" disabled={busy} className={secondaryBtn}
            onClick={() => patch({ published: !a.published }, a.published ? "Am ascuns tema de la cursanți." : "Tema e publicată.")}>
            {a.published ? "Ascunde de la cursanți" : "Publică"}
          </button>
          <ArchiveButton busy={busy} question="Arhivezi tema? Nu mai apare la cursanți; predările rămân păstrate."
            onArchive={() => patch({ archived: true }, "Am arhivat tema.", () => navigate("/portal"))} />
        </div>
      </div>
    </section>
  );
}

/** "Arhivează", asked once more (19.10.2026): it takes the item away from the participants at once. */
function ArchiveButton({ busy, question, onArchive }) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <button type="button" disabled={busy} onClick={() => setAsking(true)} data-testid="portal-archive"
        className="px-3 text-sm font-semibold text-[#c0483f] hover:underline">
        Arhivează
      </button>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-2 text-sm text-[#373A45]" role="group" aria-label="Confirmă arhivarea">
      {question}
      <button type="button" disabled={busy} onClick={onArchive} data-testid="portal-archive-confirm"
        className="rounded-full bg-[#c0483f] px-3 py-1 font-semibold text-white hover:bg-[#a33a32]">
        Da, arhivează
      </button>
      <button type="button" disabled={busy} onClick={() => setAsking(false)}
        className="px-2 font-semibold text-[#6B7280] hover:text-[#373A45]">
        Renunță
      </button>
    </span>
  );
}

// --------------------------------------------------------------- material

function Material({ activity }) {
  const [error, setError] = useState("");
  const files = activity.files || [];
  const links = activity.links || [];
  return (
    <section className="mt-6 grid gap-3" data-testid="portal-material">
      {error && <p className="text-sm text-[#c0483f]" role="alert">{error}</p>}
      {files.length === 0 && links.length === 0 && <p className="text-sm text-[#6B7280]">Încă nimic atașat.</p>}
      {(files.length > 0 || links.length > 0) && (
        <ul className="grid gap-2 rounded-2xl border border-[#E5E7EB] bg-white p-5">
          {files.map((f) => (
            <li key={f.id}>
              <button type="button" onClick={() => downloadFile(f.id, setError)}
                className="inline-flex items-center gap-2 text-left text-sm font-semibold text-[#373A45] hover:text-[#965EA7]">
                <FileText size={15} className="shrink-0" /> {f.name}
                <span className="text-xs font-normal text-[#6B7280]">{formatBytes(f.size)}</span>
              </button>
            </li>
          ))}
          <LinkList links={links} />
        </ul>
      )}
    </section>
  );
}

function MaterialEditor({ data, reload }) {
  const a = data.activity;
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: a.title, description: a.description || "" });
  const [att, setAtt] = useState({ counts: Boolean(a.counts_for_attendance), hours: a.hours ? String(a.hours) : "",
    theme: a.theme ? String(a.theme) : "", component: a.component || "practical" });
  const [links, setLinks] = useState(a.links || []);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const inputRef = useRef(null);

  const patch = async (body, ok, after) => {
    setBusy(true);
    try {
      await api.patch(`/portal/activities/${a.id}`, body);
      setMessage({ tone: "good", text: ok });
      if (after) after(); else await reload();
    } catch (err) {
      setMessage({ tone: "bad", text: apiErrorMessage(err, "Nu am putut salva.") });
    } finally {
      setBusy(false);
    }
  };

  const addFiles = async (list) => {
    for (const [i, file] of Array.from(list || []).entries()) {
      const key = `${Date.now()}-${i}`;
      const problem = fileProblem(file);
      setUploads((u) => [...u, { key, name: file.name, pct: problem ? null : 0, error: problem }]);
      if (problem) continue; // eslint-disable-line no-continue
      try {
        await uploadFile(`/portal/activities/${a.id}/files/uploads`, file, { // eslint-disable-line no-await-in-loop
          onProgress: (pct) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct } : x))),
        });
        setUploads((u) => u.filter((x) => x.key !== key));
      } catch (err) {
        setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct: null, error: uploadErrorText(err, apiErrorMessage) } : x)));
      }
    }
    await reload();
  };

  // A link is saved as soon as it is added or removed, like a file (19.10.2026):
  // before, it waited for "Salvează" and was lost if the page was left.
  const saveLinks = (next, ok) => {
    setLinks(next);
    return patch({ links: next.map(({ url, label }) => ({ url, label })) }, ok);
  };

  const addLink = async () => {
    const problem = linkProblem(linkUrl);
    if (problem) { setMessage({ tone: "bad", text: problem }); return; }
    await saveLinks([...links, { url: linkUrl.trim(), label: linkLabel.trim() }], "Am adăugat linkul.");
    setLinkUrl("");
    setLinkLabel("");
  };

  return (
    <section className="mt-8 grid gap-4" data-testid="portal-material-editor">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-xl font-bold text-[#373A45]">Pentru formator</h2>
        <Chip tone={a.published ? "good" : "neutral"}>{a.published ? "Publicat" : "Nepublicat"}</Chip>
      </div>
      <div className="grid gap-4 rounded-2xl border border-[#E5E7EB] bg-white p-5">
        <label className="grid gap-1 text-sm font-semibold text-[#373A45]">
          Titlu
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={160} className={fieldCls} />
        </label>
        <label className="grid gap-1 text-sm font-semibold text-[#373A45]">
          Descriere
          <FormattedTextarea value={form.description} onChange={(description) => setForm({ ...form, description })} rows={5} className={fieldCls} />
        </label>

        <div>
          <p className="text-sm font-semibold text-[#373A45]">Fișiere</p>
          {(a.files || []).length > 0 && (
            <ul className="mt-2 grid gap-1.5">
              {a.files.map((f) => (
                <li key={f.id} className="flex items-center gap-2 rounded-lg bg-[#F5F6F6] px-3 py-2 text-sm">
                  <FileText size={15} className="shrink-0 text-[#6B7280]" />
                  <span className="min-w-0 flex-1 truncate text-[#373A45]">{f.name}</span>
                  <span className="text-xs tabular-nums text-[#6B7280]">{formatBytes(f.size)}</span>
                  <button type="button" aria-label={`Scoate ${f.name}`} className="rounded p-1 text-[#6B7280] hover:text-[#c0483f]"
                    onClick={() => patch({ files: a.files.filter((x) => x.id !== f.id).map((x) => x.id) }, "Am scos fișierul.")}>
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {uploads.map((u) => (
            <p key={u.key} className={`mt-1.5 text-xs ${u.error ? "text-[#c0483f]" : "text-[#6B7280]"}`}>{u.name}: {u.error || `${u.pct}%`}</p>
          ))}
          <button type="button" onClick={() => inputRef.current?.click()} className={`mt-2 ${secondaryBtn}`}>
            <UploadCloud size={14} /> Adaugă fișiere
          </button>
          <p className="mt-1 text-xs text-[#6B7280]">{ALLOWED_LABEL}. Până la 50 MB pe fișier.</p>
          <input ref={inputRef} type="file" multiple accept={ACCEPT_ATTR} className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="portal-material-input" />
        </div>

        <div>
          <p className="text-sm font-semibold text-[#373A45]">Linkuri</p>
          {links.length > 0 && (
            <ul className="mt-2 grid gap-1.5">
              {links.map((ln, i) => (
                <li key={`${ln.url}-${i}`} className="flex items-center gap-2 rounded-lg bg-[#F5F6F6] px-3 py-2 text-sm">
                  <Link2 size={15} className="shrink-0 text-[#6B7280]" />
                  <span className="min-w-0 flex-1 truncate text-[#373A45]">{ln.label || ln.url}</span>
                  <span className="text-xs text-[#6B7280]">{hostOf(ln.url)}</span>
                  <button type="button" disabled={busy} onClick={() => saveLinks(links.filter((_, j) => j !== i), "Am scos linkul.")} aria-label="Scoate linkul"
                    className="rounded p-1 text-[#6B7280] hover:text-[#c0483f]"><X size={14} /></button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_12rem_auto]">
            <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" className={fieldCls} inputMode="url" />
            <input value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} placeholder="Ce e (opțional)" maxLength={120} className={fieldCls} />
            <button type="button" onClick={addLink} disabled={busy} className={secondaryBtn}>Adaugă</button>
          </div>
        </div>

        {!isFped(data) && <AttendanceFields value={att} onChange={setAtt} kind="resource" />}
        {message && (
          <p className={`text-sm ${message.tone === "bad" ? "text-[#c0483f]" : "text-[#3c7457]"}`} role="status">{message.text}</p>
        )}
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy} className={primaryBtn}
            onClick={() => patch({ title: form.title, description: form.description,
              links: links.map(({ url, label }) => ({ url, label })),
              counts_for_attendance: att.counts, hours: att.counts ? parseHours(att.hours) || 0 : 0,
        theme: att.theme ? Number(att.theme) : null, component: att.component || "practical" }, "Am salvat.")}>
            Salvează
          </button>
          <button type="button" disabled={busy} className={secondaryBtn}
            onClick={() => patch({ published: !a.published }, a.published ? "Am ascuns materialul." : "Materialul e publicat.")}>
            {a.published ? "Ascunde de la cursanți" : "Publică"}
          </button>
          <ArchiveButton busy={busy} question="Arhivezi materialul? Nu mai apare la cursanți."
            onArchive={() => patch({ archived: true }, "Am arhivat materialul.", () => navigate("/portal"))} />
        </div>
      </div>
    </section>
  );
}
